import React, { useState } from 'react';
import { Button } from 'src/components/ui/button';


import { CircleCheck, CloudDownload, EyeOff, Flag, FlagOff, ListMinus, ListPlus, MoreHorizontal, PencilLine } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'src/components/ui/dropdown-menu';
import { Invoice, InvoiceLineItem } from 'src/redux/billing/billing.types';
import { useGetInvoiceGroupDetailsQuery, useSyncInvoiceMutation } from 'src/redux/billing/billing.endpoints';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'src/components/ui/dialog';
import FilteredInputDropdown from 'src/components/ui/filtered-input-dropdown';
import { Company } from 'src/pages/billing/types/billing.types';

interface InvoiceOptionsDropdownProps {
  invoice: Invoice;
  selectedGroupId: string;
  updateInvoiceField: (invoiceId: string, field: string, value: any) => void;
}

interface InvoiceLineItemAssignmentModalProps {
  invoiceLineItem: InvoiceLineItem;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  invoices: Invoice[];
  updateLineItemField: (lineItemId: string, field: string, value: any) => void;
  companies: Company[];
}

const InvoiceLineItemAssignmentModal: React.FC<InvoiceLineItemAssignmentModalProps> = ({
  invoiceLineItem,
  isOpen,
  onOpenChange,
  invoices,
  companies,
  updateLineItemField,
}) => {
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);

  const getCompanyName = (companyId: string): string => {
    const company = companies.find((company) => company.id === companyId);
    return company?.company_name || companyId;
  };

  const handleReassign = () => {
    if (selectedCompanyId !== invoiceLineItem.invoice) {
      updateLineItemField(invoiceLineItem.id, 'invoice', selectedCompanyId);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={onOpenChange}
    >
      <DialogContent className="sm:max-w-md bg-white">
        <DialogHeader>
          <DialogTitle>
            Assign to Invoice
          </DialogTitle>
          <DialogDescription>
            Assign the line item to a new invoice.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <FilteredInputDropdown
              inputClassName='bg-gray-300 rounded-xl'
              options={invoices?.map((invoice) => ({
                value: invoice.id,
                label: getCompanyName(invoice.company),
              })) || []}
              selectedValue={selectedCompanyId || undefined}
              onSelect={(value: string) => {
                setSelectedCompanyId(value);
              }}
              placeholder="Search for a company..."
            />

          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant='create'
            onClick={handleReassign}
          >
            Reassign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const InvoiceOptionsDropdown: React.FC<InvoiceOptionsDropdownProps> = ({
  invoice,
  selectedGroupId,
  updateInvoiceField,
}) => {

  const { refetch: refetchInvoiceGroupDetails } = useGetInvoiceGroupDetailsQuery(selectedGroupId, {
    skip: !selectedGroupId,
  });
  const [syncInvoice] = useSyncInvoiceMutation();

  const handleRefreshInvoices = async() => {
    if (refetchInvoiceGroupDetails) {
      await refetchInvoiceGroupDetails();
    }
  };

  const handleSyncInvoice = async() => {
    if (syncInvoice && invoice) {
      await syncInvoice({ id: invoice.id });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-8 w-8 p-0"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <span className="sr-only">
            Open menu
          </span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">

        {/* flag */}
        {!invoice.attention_needed ? (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              updateInvoiceField(invoice.id, 'attention_needed', true);
            }}
            className="cursor-pointer"
          >
            <Flag className="h-4 w-4" />
            Attention Needed
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              updateInvoiceField(invoice.id, 'attention_needed', false);
            }}
            className="cursor-pointer"
          >
            <FlagOff className="h-4 w-4" />
            Resolved
          </DropdownMenuItem>
        )}


        {/* hide */}
        {invoice.hide ? (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              updateInvoiceField(invoice.id, 'hide', false);
            }}
            className="cursor-pointer"
          >
            <EyeOff className="h-4 w-4" />
            Show
          </DropdownMenuItem>
        ) : (
          // show
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              updateInvoiceField(invoice.id, 'hide', true);
            }}
            className="cursor-pointer"
          >
            <EyeOff className="h-4 w-4" />
            Hide
          </DropdownMenuItem>
        )}

        {/* sync */}
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleSyncInvoice();
          }}
          className="cursor-pointer"
        >
          <CloudDownload className="h-4 w-4" />
          Sync
        </DropdownMenuItem>

        {invoice.invoice_status?.toLowerCase() === 'approved' ? (
          // draft
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              updateInvoiceField(invoice.id, 'invoice_status', 'draft');
            }}
            className="cursor-pointer"
          >
            <PencilLine className="h-4 w-4" />
            Set as Draft
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              updateInvoiceField(invoice.id, 'invoice_status', 'approved');
            }}
            className="cursor-pointer"
          >
            <CircleCheck className="h-4 w-4" />
            Approve
          </DropdownMenuItem>
        )}

      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface InvoiceLineItemOptionsDropdownProps {
  lineItem: InvoiceLineItem;
  updateLineItemField: (lineItemId: string, field: string, value: any) => void;
  invoices: Invoice[];
  companies: Company[];
}

const InvoiceLineItemOptionsDropdown: React.FC<InvoiceLineItemOptionsDropdownProps> = ({
  lineItem,
  updateLineItemField,
  invoices,
  companies,
}) => {

  const [isShow, setIsShow] = useState(false);

  const handleUnassignLineItem = (lineItem: InvoiceLineItem) => {
    const unassignedCompany = companies.find(company => company.company_name === 'Unassigned');
    const unassignedInvoice = invoices.find(invoice => invoice.company === unassignedCompany?.id);
    if (unassignedInvoice) {
      updateLineItemField(lineItem.id, 'invoice', unassignedInvoice?.id);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-8 w-8 p-0"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <span className="sr-only">
            Open menu
          </span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {/* unassign */}
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleUnassignLineItem(lineItem);
          }}
          className="cursor-pointer"
        >
          <ListMinus className="h-4 w-4" />
          Unassign
        </DropdownMenuItem>


        {/* reassign */}
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            setIsShow(true);
          }}
          className="cursor-pointer"
        >
          <ListPlus className="h-4 w-4" />
          Assign to
        </DropdownMenuItem>

        {/* flag */}
        {!lineItem.attention_needed ? (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              updateLineItemField(lineItem.id, 'attention_needed', true);
            }}
            className="cursor-pointer"
          >
            <Flag className="h-4 w-4" />
            Attention Needed
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              updateLineItemField(lineItem.id, 'attention_needed', false);
            }}
            className="cursor-pointer"
          >
            <FlagOff className="h-4 w-4" />
            Resolved
          </DropdownMenuItem>
        )}


        {/* hide */}
        {lineItem.hide ? (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              updateLineItemField(lineItem.id, 'hide', false);
            }}
            className="cursor-pointer"
          >
            <EyeOff className="h-4 w-4" />
            Show
          </DropdownMenuItem>
        ) : (
          // show
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              updateLineItemField(lineItem.id, 'hide', true);
            }}
            className="cursor-pointer"
          >
            <EyeOff className="h-4 w-4" />
            Hide
          </DropdownMenuItem>
        )}

        {lineItem.invoice_line_item_status?.toLowerCase() === 'approved' ? (
          // draft
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              updateLineItemField(lineItem.id, 'invoice_line_item_status', 'draft');
            }}
            className="cursor-pointer"
          >
            <PencilLine className="h-4 w-4" />
            Set as Draft
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              updateLineItemField(lineItem.id, 'invoice_line_item_status', 'approved');
            }}
            className="cursor-pointer"
          >
            <CircleCheck className="h-4 w-4" />
            Approve
          </DropdownMenuItem>
        )}

      </DropdownMenuContent>

      <InvoiceLineItemAssignmentModal
        invoiceLineItem={lineItem}
        isOpen={isShow}
        onOpenChange={(val) => setIsShow(val)}
        invoices={invoices}
        updateLineItemField={updateLineItemField}
        companies={companies}
      />
    </DropdownMenu>
  );
};

export { InvoiceOptionsDropdown, InvoiceLineItemOptionsDropdown };


